import {
  model,
  Model,
  prop,
  idProp,
  rootRef,
  Ref,
  detach,
  findParent,
  modelFlow,
  modelAction,
  _async,
  Frozen,
  frozen,
} from "mobx-keystone";
import { computed } from "mobx";
import { Bob } from "./Bob";
import { sendToBobController } from "../../helpers/requests";
import {
  BobSponsorshipVideoProposal,
  BobSponsorshipVideoProposalRef,
} from "./BobSponsorshipVideoProposal";

export interface SponsorshipVideo {
  url: string | null;
  views: number | null;
  is_valid: boolean;
  description: string | null;
}

export interface SponsorshipRedemption {
  amount_satoshis: number;
  kind: string;
  created_at: string;
}

@model("exrails/Sponsorship")
export class Sponsorship extends Model({
  _version: prop(0),
  id: idProp,
  satoshisPerView: prop<number>(0),
  startingBonusSatoshis: prop<number>(0),
  tokuserName: prop<string>(""),
  maximumViews: prop<number>(0),
  startingBonusRedeemedAt: prop<string | null>(null),
  isAllowedToScrape: prop<boolean>(false),
  nextScrapeAllowedAt: prop<string | null>(null),
  bonusRedemptionError: prop<string | null>(null),
  bonusRedemptionSuccess: prop<string | null>(null),
  videoViewsUpdateError: prop<string | null>(null),
  videoViewsUpdateSuccess: prop<string | null>(null),
  viewRedemptionError: prop<string | null>(null),
  viewRedemptionSuccess: prop<string | null>(null),
  availableRedemptionSatoshis: prop<number>(0),
  hasReachedMaximumViews: prop<boolean>(false),
  // submitted videos
  videos: prop<Frozen<SponsorshipVideo[]>>(() =>
    frozen<SponsorshipVideo[]>([]),
  ),
  // redemptions
  redemptions: prop<Frozen<SponsorshipRedemption[]>>(() =>
    frozen<SponsorshipRedemption[]>([]),
  ),
  // video proposals
  selectedVideoProposalRef: prop<Ref<BobSponsorshipVideoProposal> | undefined>(
    undefined,
  ),
  videoProposals: prop<BobSponsorshipVideoProposal[]>(() => []),
}) {
  get bob() {
    return findParent<Bob>(this, (parentNode) => parentNode instanceof Bob);
  }

  @modelAction
  setBonusRedemptionError(message: string | null) {
    this.bonusRedemptionError = message;
  }

  @modelAction
  setBonusRedemptionSuccess(message: string | null) {
    this.bonusRedemptionSuccess = message;
  }

  @modelAction
  setVideos(data: SponsorshipVideo[]) {
    this.videos = frozen(data);
  }

  @modelAction
  setRedemptions(data: SponsorshipRedemption[]) {
    this.redemptions = frozen(data);
  }

  @modelAction
  setIsAllowedToScrape(value: boolean) {
    this.isAllowedToScrape = value;
  }

  @modelAction
  setNextScrapeAllowedAt(value: string | null) {
    this.nextScrapeAllowedAt = value;
  }

  @modelAction
  setVideoViewsUpdateError(message: string | null) {
    this.videoViewsUpdateError = message;
  }

  @modelAction
  setVideoViewsUpdateSuccess(message: string | null) {
    this.videoViewsUpdateSuccess = message;
  }

  @modelAction
  setStartingBonusRedeemedAt(value: string) {
    this.startingBonusRedeemedAt = value;
  }

  @modelAction
  setAvailableRedemptionSatoshis(value: number) {
    this.availableRedemptionSatoshis = value;
  }

  @modelAction
  setHasReachedMaximumViews(value: boolean) {
    this.hasReachedMaximumViews = value;
  }

  @modelAction
  setViewRedemptionError(message: string | null) {
    this.viewRedemptionError = message;
  }

  @modelAction
  setViewRedemptionSuccess(message: string | null) {
    this.viewRedemptionSuccess = message;
  }

  // BobSponsorshipVideoProposal methods
  @computed
  get selectedVideoProposal() {
    return this.selectedVideoProposalRef
      ? this.selectedVideoProposalRef.current
      : undefined;
  }

  @modelAction
  selectVideoProposal(proposal: BobSponsorshipVideoProposal | undefined) {
    if (proposal && !this.videoProposals.includes(proposal))
      throw new Error("unknown videoProposal");
    this.selectedVideoProposalRef = proposal
      ? BobSponsorshipVideoProposalRef(proposal)
      : undefined;
  }

  @modelAction
  deleteAllVideoProposals() {
    this.selectedVideoProposalRef = undefined;
    this.videoProposals = [];
  }

  @modelAction
  createAndSelectNewVideoProposal() {
    this.deleteAllVideoProposals();
    const proposal = new BobSponsorshipVideoProposal({});
    this.videoProposals.push(proposal);
    this.selectVideoProposal(proposal);
    return proposal;
  }

  @modelFlow
  updateVideoViews = _async(function* (this: Sponsorship) {
    this.bob!.setAreWeWaitingOnRails(true);
    this.videoViewsUpdateError = null;
    this.videoViewsUpdateSuccess = null;
    const message = {
      bobUuid: this.bob!.uuid,
    };
    sendToBobController(message, "/bob_sponsorship/update_video_views")
      .then((response) => {
        this.bob!.setAreWeWaitingOnRails(false);
        if (response.success === false) {
          this.setVideoViewsUpdateError(response.message);
          return;
        }
        this.setVideoViewsUpdateSuccess(response.message);
        this.setVideos(response.sponsorship.videos || []);
        this.setIsAllowedToScrape(response.sponsorship.is_allowed_to_scrape);
        this.setNextScrapeAllowedAt(
          response.sponsorship.next_scrape_allowed_at,
        );
        this.setAvailableRedemptionSatoshis(
          response.sponsorship.available_redemption_satoshis || 0,
        );
        this.setHasReachedMaximumViews(
          response.sponsorship.has_reached_maximum_views || false,
        );
      })
      .catch((error) => {
        this.bob!.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelFlow
  redeemStartingBonus = _async(function* (this: Sponsorship) {
    this.bob.setAreWeWaitingOnRails(true);
    this.bonusRedemptionError = null;
    const message = {
      bobUuid: this.bob.uuid,
    };
    sendToBobController(message, "/bob_sponsorship/redeem_starting_bonus")
      .then((response) => {
        this.bob.setAreWeWaitingOnRails(false);
        if (response.success === false) {
          this.setBonusRedemptionError(response.message);
          return;
        }
        this.setStartingBonusRedeemedAt(response.starting_bonus_redeemed_at);
        this.bob.setPlatformCredits(response.platform_credits);
        this.setRedemptions(response.redemptions || []);
        this.setBonusRedemptionSuccess(response.message);
      })
      .catch((error) => {
        this.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelFlow
  redeemViews = _async(function* (this: Sponsorship) {
    this.bob!.setAreWeWaitingOnRails(true);
    this.viewRedemptionError = null;
    this.viewRedemptionSuccess = null;
    const message = {
      bobUuid: this.bob!.uuid,
    };
    sendToBobController(message, "/bob_sponsorship/redeem_views")
      .then((response) => {
        this.bob!.setAreWeWaitingOnRails(false);
        if (response.success === false) {
          this.setViewRedemptionError(response.message);
          return;
        }
        this.setViewRedemptionSuccess(response.message);
        this.bob!.setPlatformCredits(response.platform_credits);
        this.setVideos(response.sponsorship.videos || []);
        this.setRedemptions(response.sponsorship.redemptions || []);
        this.setAvailableRedemptionSatoshis(
          response.sponsorship.available_redemption_satoshis || 0,
        );
        this.setHasReachedMaximumViews(
          response.sponsorship.has_reached_maximum_views || false,
        );
      })
      .catch((error) => {
        this.bob!.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });
}

export const SponsorshipRef = rootRef<Sponsorship>("exrails/SponsorshipRef", {
  onResolvedValueChange(ref, newThing, oldThing) {
    if (newThing && !oldThing) {
      detach(ref);
    }
  },
});
